import { useFormContext } from 'react-hook-form'
import { FormField } from '@/components/serge/FormField'
import { type LegalEntityFormData } from '@/lib/legalEntitySchema'

const BUSINESS_MODELS = [
  { value: '', label: 'Sélectionner...' },
  { value: 'B2C', label: 'B2C - Vente aux particuliers' },
  { value: 'B2B', label: 'B2B - Vente aux professionnels' },
  { value: 'B2B2C', label: 'B2B2C - Mixte professionnels et particuliers' },
  { value: 'SaaS B2B', label: 'SaaS B2B - Logiciel en ligne pour entreprises' },
  { value: 'SaaS B2C', label: 'SaaS B2C - Logiciel en ligne grand public' },
  { value: 'E-commerce B2C', label: 'E-commerce B2C - Vente en ligne aux particuliers' },
  { value: 'E-commerce B2B', label: 'E-commerce B2B - Vente en ligne aux professionnels' },
  { value: 'Marketplace', label: 'Marketplace - Plateforme d\'intermédiation' },
  { value: 'Services B2B', label: 'Prestations de services aux entreprises' },
  { value: 'Services B2C', label: 'Prestations de services aux particuliers' },
]

export function CGVStep() {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<LegalEntityFormData>()

  const paymentMethods = watch('paymentMethods') || []
  const businessModel = watch('businessModel') || ''

  // Détection automatique B2C pour afficher les avertissements
  const isB2C = /b2c|particulier|grand public/i.test(businessModel)
  const isSaaS = /saas|abonnement/i.test(businessModel)

  return (
    <div className="space-y-6">
      <div className="border border-border bg-surface p-5">
        <h3 className="text-lg font-semibold text-foreground mb-1">Conditions Générales de Vente / Utilisation</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Informations commerciales et contractuelles pour la génération automatique de vos CGV
        </p>

        {isB2C && (
          <div className="mb-4 p-4 bg-amber-900/20 border border-amber-500/30 rounded">
            <p className="text-sm text-amber-200">
              <strong>Activité B2C détectée :</strong> Les CGV générées incluront automatiquement les mentions obligatoires du Code de la consommation (droit de rétractation, médiation, etc.)
            </p>
          </div>
        )}

        {isSaaS && (
          <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded">
            <p className="text-sm text-blue-200">
              <strong>Modèle SaaS détecté :</strong> Les CGV incluront des clauses spécifiques (disponibilité du service, maintenance, gestion des données, conditions d'abonnement).
            </p>
          </div>
        )}

        <div className="space-y-4">
          <FormField
            label="Modèle d'activité"
            error={errors.businessModel}
            tooltip="Détermine les clauses obligatoires à inclure dans vos CGV"
            required
          >
            <select
              {...register('businessModel')}
              className="w-full border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition"
            >
              {BUSINESS_MODELS.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Description du service / produit"
            error={errors.serviceDescription}
            tooltip="Description détaillée de ce que vous proposez"
          >
            <textarea
              {...register('serviceDescription')}
              className="w-full min-h-[120px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
              placeholder="Décrivez précisément les services ou produits proposés..."
            />
          </FormField>

          <FormField
            label="Moyens de paiement acceptés"
            hint="Un moyen par ligne (ex: Carte bancaire, Virement, PayPal)"
            tooltip="Liste des méthodes de paiement disponibles pour vos clients"
          >
            <textarea
              value={(paymentMethods ?? []).join('\n')}
              onChange={(e) => {
                const values = e.target.value.split('\n').map((line) => line.trim())
                setValue('paymentMethods', values, { shouldDirty: true })
              }}
              className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
              placeholder="Carte bancaire (CB, Visa, Mastercard)&#10;Virement bancaire&#10;PayPal"
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Pénalités de retard"
              error={errors.latePaymentPenalties}
              tooltip="Conditions appliquées en cas de retard de paiement"
            >
              <textarea
                {...register('latePaymentPenalties')}
                className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                placeholder="Ex: Taux d'intérêt de 3x le taux légal + indemnité forfaitaire de 40€"
              />
            </FormField>

            <FormField
              label="Juridiction / Droit applicable"
              error={errors.jurisdiction}
              tooltip="Tribunal compétent et droit applicable en cas de litige"
            >
              <input
                {...register('jurisdiction')}
                className="w-full border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition"
                placeholder="Ex: Tribunaux de Paris, Droit français"
              />
            </FormField>
          </div>

          {/* Section rétractation/remboursement - mise en avant pour B2C */}
          {isB2C && (
            <div className="p-4 bg-surface border border-border rounded">
              <h4 className="text-sm font-medium text-foreground mb-3">
                Droits des consommateurs (obligatoire B2C)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Politique de rétractation"
                  error={errors.withdrawalPolicy}
                  tooltip="Délai légal de 14 jours pour les ventes à distance (article L221-18 du Code de la consommation)"
                  required
                >
                  <textarea
                    {...register('withdrawalPolicy')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Droit de rétractation de 14 jours à compter de la réception du bien ou de l'acceptation de l'offre pour les services, conformément aux articles L221-18 et suivants du Code de la consommation."
                  />
                </FormField>

                <FormField
                  label="Politique de remboursement"
                  error={errors.refundPolicy}
                  tooltip="Délai légal de remboursement : 14 jours maximum après notification de la rétractation"
                  required
                >
                  <textarea
                    {...register('refundPolicy')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Remboursement intégral sous 14 jours suivant la notification de rétractation, par le même moyen de paiement utilisé lors de la commande."
                  />
                </FormField>
              </div>
            </div>
          )}

          {!isB2C && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="Politique de rétractation"
                error={errors.withdrawalPolicy}
                tooltip="Non obligatoire pour les ventes B2B"
              >
                <textarea
                  {...register('withdrawalPolicy')}
                  className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                  placeholder="Ex: Pas de droit de rétractation applicable aux professionnels"
                />
              </FormField>

              <FormField
                label="Politique de remboursement"
                error={errors.refundPolicy}
                tooltip="Conditions de remboursement pour vos clients professionnels"
              >
                <textarea
                  {...register('refundPolicy')}
                  className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                  placeholder="Ex: Remboursement sous conditions selon les termes du contrat"
                />
              </FormField>
            </div>
          )}

          <FormField
            label="Médiation et recours"
            error={errors.mediationInfo}
            tooltip={isB2C ? "OBLIGATOIRE pour B2C : Vous devez désigner un médiateur de la consommation" : "Facultatif pour les ventes B2B"}
            required={isB2C}
            hint={isB2C ? "Obligation légale depuis le 1er janvier 2016 (article L612-1 du Code de la consommation)" : undefined}
          >
            <textarea
              {...register('mediationInfo')}
              className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
              placeholder={isB2C
                ? "Nom du médiateur : [Nom]&#10;Adresse : [Adresse complète]&#10;Site web : [URL]&#10;Email : [Email]"
                : "Facultatif - Préciser les modalités de règlement des litiges"
              }
            />
          </FormField>
        </div>
      </div>
    </div>
  )
}
